import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  CheckCircle,
  Copy,
  DotsThreeVertical,
  FilePdf,
  ForkKnife,
  Plus,
  Trash,
} from "@phosphor-icons/react";
import {
  useGetMenusForPatientQuery,
  useDuplicateMenuMutation,
  useDeleteMenuMutation,
  useSetActiveMenuMutation,
} from "../../../store/services/menusApi";
import {
  generateMenuPdf,
  buildMenuPdfPayload,
} from "../../../firebase";
import {
  selectTherapistId,
  selectTherapistName,
} from "../../../store/slices/authSlice";
import { useToast } from "../../../contexts/ToastContext";
import ConfirmDialog from "../../ui/ConfirmDialog";
import EmptyDraw from "../../ui/EmptyDraw";
import { formatHebrewDate } from "../../../utils/dateUtils";
import "./MenusTab.css";
import ContentLoader from "../../shared/ContentLoader";

/**
 * "תפריטים" — the patient's menus, with the one active menu badged.
 *
 * Active-ness is `patient.nutrition.activeMenuId`, a single field on the
 * patient document, so exactly one menu can be active by construction.
 */

const MACROS = [
  { key: "calories", label: "קלוריות", unit: "" },
  { key: "carbohydrates", label: "פחמימות", unit: "ג׳" },
  { key: "protein", label: "חלבון", unit: "ג׳" },
  { key: "fat", label: "שומן", unit: "ג׳" },
];

function MenusTab({ clinicId, patientId, patient }) {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const therapistId = useSelector(selectTherapistId);
  const therapistName = useSelector(selectTherapistName);

  const [openMenuId, setOpenMenuId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [activateTarget, setActivateTarget] = useState(null);
  const [exportingId, setExportingId] = useState(null);

  const {
    data: menus = [],
    isLoading,
    isError,
  } = useGetMenusForPatientQuery(
    { clinicId, patientId },
    { skip: !clinicId || !patientId }
  );

  const [duplicateMenu, { isLoading: isDuplicating }] = useDuplicateMenuMutation();
  const [deleteMenu, { isLoading: isDeleting }] = useDeleteMenuMutation();
  const [setActiveMenu, { isLoading: isActivating }] = useSetActiveMenuMutation();

  const activeMenuId = patient?.nutrition?.activeMenuId || null;
  const isBusy = isDuplicating || isDeleting || isActivating || !!exportingId;

  const openBuilder = (menuId) =>
    navigate(
      menuId
        ? `/dashboard/patients/${patientId}/menu/${menuId}`
        : `/dashboard/patients/${patientId}/menu`
    );

  const handleDuplicate = async (menu) => {
    setOpenMenuId(null);
    try {
      const copy = await duplicateMenu({
        clinicId,
        patientId,
        menuId: menu.id,
        therapistId,
        therapistName,
      }).unwrap();
      showSuccess("התפריט שוכפל");
      openBuilder(copy.id);
    } catch (error) {
      console.error("[CK-10080] Error duplicating menu:", {
        errorCode: "CK-10080",
        errorMessage: error?.message,
        clinicId,
        patientId,
        menuId: menu.id,
        timestamp: new Date().toISOString(),
      });
      showError("שגיאה בשכפול התפריט");
    }
  };

  const handleActivate = async () => {
    if (!activateTarget) return;
    try {
      await setActiveMenu({
        clinicId,
        patientId,
        menuId: activateTarget.id,
        therapistId,
        therapistName,
      }).unwrap();
      showSuccess("התפריט הוגדר כתפריט הפעיל");
    } catch (error) {
      console.error("[CK-10081] Error setting the active menu:", {
        errorCode: "CK-10081",
        errorMessage: error?.message,
        clinicId,
        patientId,
        menuId: activateTarget.id,
        timestamp: new Date().toISOString(),
      });
      showError("שגיאה בהגדרת התפריט הפעיל");
    } finally {
      setActivateTarget(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    // The active menu is never deletable from the UI — another menu must be
    // marked active first (the helper's defensive clear stays for legacy data)
    if (deleteTarget.id === activeMenuId) {
      showError("זהו התפריט הפעיל. כדי למחוק אותו יש לסמן קודם תפריט אחר כפעיל.");
      setDeleteTarget(null);
      return;
    }
    try {
      const result = await deleteMenu({
        clinicId,
        patientId,
        menuId: deleteTarget.id,
        therapistId,
        therapistName,
      }).unwrap();
      // The server clears activeMenuId when the deleted menu was the active
      // one — say so, because it changes what the client sees.
      showSuccess(
        result?.clearedActive
          ? "התפריט נמחק. ללקוח/ה אין כעת תפריט פעיל"
          : "התפריט נמחק"
      );
    } catch (error) {
      console.error("[CK-10082] Error deleting menu:", {
        errorCode: "CK-10082",
        errorMessage: error?.message,
        clinicId,
        patientId,
        menuId: deleteTarget.id,
        timestamp: new Date().toISOString(),
      });
      showError("שגיאה במחיקת התפריט");
    } finally {
      setDeleteTarget(null);
    }
  };

  const handleExportPdf = async (menu) => {
    setOpenMenuId(null);
    setExportingId(menu.id);
    try {
      const patientName = [patient?.firstName, patient?.lastName]
        .filter(Boolean)
        .join(" ");
      const result = await generateMenuPdf({
        clinicId,
        patientId,
        menuId: menu.id,
        menu: buildMenuPdfPayload(menu, patientName),
      });
      if (result?.pdfUrl) {
        window.open(result.pdfUrl, "_blank", "noopener,noreferrer");
        showSuccess("קובץ ה-PDF נוצר");
      }
    } catch (error) {
      console.error("[CK-10084] Error exporting a menu to PDF:", {
        errorCode: "CK-10084",
        errorMessage: error?.message,
        clinicId,
        patientId,
        menuId: menu.id,
        timestamp: new Date().toISOString(),
      });
      showError("ייצוא ה-PDF נכשל, נסה/י שוב");
    } finally {
      setExportingId(null);
    }
  };

  if (isLoading) {
    return (
      <ContentLoader fill message="טוען תפריטים..." />
    );
  }

  if (isError) {
    return (
      <div className="nmnu-error">
        <p>שגיאה בטעינת התפריטים. נסה/י לרענן את הדף.</p>
      </div>
    );
  }

  return (
    <div className="nmnu-tab">
      <div className="nmnu-header">
        <div className="nmnu-header-text">
          <h3>
            <ForkKnife size={20} weight="fill" />
            תפריטים
          </h3>
          <p>
            {activeMenuId
              ? "הלקוח/ה רואה בעמוד האישי רק את התפריט הפעיל"
              : "אין תפריט פעיל — הלקוח/ה לא רואה תפריט בעמוד האישי"}
          </p>
        </div>

        <div className="nmnu-header-actions">

          <button
            type="button"
            className="nmnu-primary-btn"
            onClick={() => openBuilder(null)}
          >
            <Plus size={16} weight="bold" />
            תפריט חדש
          </button>
        </div>
      </div>

      {menus.length === 0 ? (
        <div className="nmnu-empty">
          <EmptyDraw variant="treatment" />
          <p>
            עדיין אין תפריטים. התפריט הראשון שתבנו יהפוך אוטומטית לתפריט הפעיל —
            זה מה שהלקוח/ה יראה/תראה בעמוד האישי.
          </p>
          <button
            type="button"
            className="nmnu-primary-btn"
            onClick={() => openBuilder(null)}
          >
            <Plus size={16} weight="bold" />
            בניית תפריט ראשון
          </button>
        </div>
      ) : (
        <div className="nmnu-list">
          {menus.map((menu) => {
            const isActive = menu.id === activeMenuId;
            return (
              <div
                key={menu.id}
                className={`nmnu-card ${isActive ? "is-active" : ""}`}
              >
                {isActive && (
                  <span className="nmnu-ribbon" aria-label="תפריט פעיל">
                    <CheckCircle size={12} weight="fill" />
                    תפריט פעיל
                  </span>
                )}
                <button
                  type="button"
                  className="nmnu-card-main"
                  onClick={() => openBuilder(menu.id)}
                >
                  <div className="nmnu-card-head">
                    <span className="nmnu-card-name">{menu.menuName}</span>

                    {menu.isAveragesMenu && (
                      <span className="nmnu-badge">תפריט ממוצעים</span>
                    )}
                  </div>

                  <div className="nmnu-macros">
                    {MACROS.map(({ key, label, unit }) => (
                      <span key={key} className="nmnu-macro">
                        <span className="nmnu-macro-label">{label}</span>
                        <span className="nmnu-macro-value">
                          {menu.totals?.[key] ?? "—"}
                          {unit}
                        </span>
                      </span>
                    ))}
                  </div>

                  <span className="nmnu-card-date">
                    עודכן {formatHebrewDate(menu.updatedAt || menu.createdAt)}
                  </span>
                </button>

                <div className="nmnu-card-actions">
                  <button
                    type="button"
                    className="nmnu-icon-btn"
                    onClick={() => setOpenMenuId(openMenuId === menu.id ? null : menu.id)}
                    disabled={isBusy}
                    aria-label="פעולות נוספות"
                    aria-expanded={openMenuId === menu.id}
                  >
                    <DotsThreeVertical size={18} weight="bold" />
                  </button>

                  {openMenuId === menu.id && (
                    <>
                      <button
                        type="button"
                        className="nmnu-dropdown-scrim"
                        onClick={() => setOpenMenuId(null)}
                        aria-label="סגירת התפריט"
                      />
                      <div className="nmnu-dropdown" role="menu">
                        {!isActive && (
                          <button
                            type="button"
                            role="menuitem"
                            onClick={() => {
                              setOpenMenuId(null);
                              setActivateTarget(menu);
                            }}
                          >
                            <CheckCircle size={15} />
                            הפוך לתפריט פעיל
                          </button>
                        )}
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => handleDuplicate(menu)}
                        >
                          <Copy size={15} />
                          שכפול
                        </button>
                        {/* Phases 4 and 5 build these; shown disabled so the
                            coach knows they are coming, never as dead UI */}
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => handleExportPdf(menu)}
                          disabled={exportingId === menu.id}
                        >
                          <FilePdf size={15} />
                          {exportingId === menu.id ? "מייצא..." : "ייצוא PDF"}
                        </button>
                        <button
                          type="button"
                          role="menuitem"
                          className="nmnu-dropdown-danger"
                          onClick={() => {
                            setOpenMenuId(null);
                            setDeleteTarget(menu);
                          }}
                        >
                          <Trash size={15} />
                          מחיקה
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <ConfirmDialog
        isOpen={!!activateTarget}
        onClose={() => setActivateTarget(null)}
        onConfirm={handleActivate}
        title="הגדרת תפריט פעיל"
        message={
          activeMenuId
            ? "לכל לקוח/ה יש תפריט פעיל אחד. התפריט הפעיל הנוכחי יפסיק להופיע בעמוד האישי."
            : "התפריט יופיע בעמוד האישי של הלקוח/ה."
        }
        confirmText="הפוך לפעיל"
        cancelText="ביטול"
      />

      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="מחיקת תפריט"
        message="לא ניתן לשחזר תפריט שנמחק."
        confirmText="מחק"
        cancelText="ביטול"
        type="danger"
        icon="delete"
      />
    </div>
  );
}

export default MenusTab;
